import type { ProcessUpdate } from '@racepointenergy/swc/scs/types/host'
import { useHomeRouteParams } from 'features/home/hooks'
import { useCallback } from 'react'
import _isNil from 'lodash/isNil'
import type { CellValue, IdType } from 'react-table'

import {
  dealerConfigureProcess,
  StatusOption,
} from '@racepointenergy/swc/scs/dealer-configure-process'

import { ProcessesRowActionIcon, ProcessesStatusLabel } from '../styles'
import {
  ProcessCard,
  ProcessCardRow,
  ProcessLabel,
  MobileProcessCell,
  MobileProcessSpinnerWrapper,
} from './styles'
import { canProcessBeStopped } from './processHelper'
import { restartAction, startAction, stopAction } from '../consts'
import { Spinner } from 'components/Loader'
import { CommonLinkButton } from 'components/Button'
import { StyledIcon } from '../../HomeStatus/styles'
import { mdiArrowRight } from '@mdi/js'
import { PARENT_PROCESSES } from './consts'
import { hasSystemAccessEnabled } from 'features/home/utils'
import { useAppSelector } from 'app/hooks'
import { selectedHomeSelector } from 'features/home/selectors'
import { Status } from '@racepointenergy/swc/scs/types/host'

interface MobileProcessViewProps {
  values: Record<IdType<ProcessUpdate>, CellValue>
}

const MobileProcessViewComponent = (props: MobileProcessViewProps) => {
  const [updateConfigureProcess, { isLoading: mutationIsLoading }] =
    dealerConfigureProcess.useMutation()
  const process = props.values
  const { homeId } = useHomeRouteParams()

  const currentHome = useAppSelector(selectedHomeSelector.data)

  const hasSystemAccess = hasSystemAccessEnabled(currentHome)

  const actionCallBack = useCallback(
    (name: string, status: StatusOption) => {
      if (name) {
        updateConfigureProcess({
          id: homeId,
          status: status ?? 0,
          params: [{ process: name }],
        })
      }
    },
    [updateConfigureProcess, homeId]
  )

  const ProcessActionCell = useCallback(() => {
    const { ApplicationName: name, Status: status } = process
    if (mutationIsLoading) {
      return (
        <MobileProcessSpinnerWrapper>
          <Spinner size={1} />
        </MobileProcessSpinnerWrapper>
      )
    }
    if (
      !_isNil(name) &&
      !canProcessBeStopped(name) &&
      !PARENT_PROCESSES.includes(name)
    ) {
      return (
        <>
          {name && (
            <ProcessesRowActionIcon
              size={32}
              src={restartAction.iconUrl}
              $disabled={!hasSystemAccess}
            />
          )}
        </>
      )
    }
    const selectAction = (processStatus: Status) => {
      switch (processStatus) {
        case Status.Running:
          return (
            <>
              {name && (
                <ProcessesRowActionIcon
                  size={32}
                  src={stopAction.iconUrl}
                  $disabled={!hasSystemAccess}
                />
              )}
            </>
          )
        case Status.Stopped:
          return (
            <>
              {name && (
                <ProcessesRowActionIcon
                  size={32}
                  src={startAction.iconUrl}
                  $disabled={!hasSystemAccess}
                />
              )}
            </>
          )
        default:
          return <></>
      }
    }

    return (
      <>
        {!_isNil(name) &&
          !_isNil(status) &&
          !PARENT_PROCESSES.includes(name) &&
          selectAction(status)}
      </>
    )
  }, [process, mutationIsLoading, hasSystemAccess])

  const onActionClick = useCallback(() => {
    const { ApplicationName: name, Status: status } = process
    if (mutationIsLoading || !hasSystemAccess) {
      return
    }
    if (
      !_isNil(name) &&
      !canProcessBeStopped(name) &&
      !PARENT_PROCESSES.includes(name)
    ) {
      actionCallBack(name, StatusOption.restart)
      return
    }
    if (!_isNil(name) && !_isNil(status) && !PARENT_PROCESSES.includes(name)) {
      switch (status) {
        case Status.Running: {
          actionCallBack(name, StatusOption.stop)
          return
        }
        case Status.Stopped: {
          actionCallBack(name, StatusOption.start)
          return
        }
        default:
          return
      }
    }
  }, [process, mutationIsLoading, hasSystemAccess, actionCallBack])

  return (
    <ProcessCard>
      <ProcessCardRow>
        <ProcessLabel>
          <b>
            {process.ProcessIdentifier} {process.ApplicationName}
          </b>
        </ProcessLabel>
        <ProcessLabel>
          <CommonLinkButton>
            <StyledIcon path={mdiArrowRight} size={1} />
          </CommonLinkButton>
        </ProcessLabel>
      </ProcessCardRow>
      <ProcessCardRow>
        <ProcessLabel
          onClick={(e) => {
            e.stopPropagation()
            onActionClick()
          }}
        >
          <MobileProcessCell>{ProcessActionCell()}</MobileProcessCell>
          <ProcessesStatusLabel isRunning={!process.Status}>
            {Status[process.Status as number]}
          </ProcessesStatusLabel>
        </ProcessLabel>
        <ProcessLabel>
          <b>{process.CpuUsage?.toFixed(2)}</b>
        </ProcessLabel>
      </ProcessCardRow>
    </ProcessCard>
  )
}

export default MobileProcessViewComponent
